import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../../store/gameStore';
import { useSettingsStore } from '../../../store/settingsStore';
import { formatTime } from '../../../lib/sudoku';
import type { Difficulty } from '../../../../../shared/types';
import { cn } from '../../../lib/cn';

interface GameHeaderProps {
  onDifficultyChange: (difficulty: Difficulty) => void;
}

const difficulties: Difficulty[] = ['very_easy', 'easy', 'medium', 'hard', 'expert'];

export default function GameHeader({ onDifficultyChange }: GameHeaderProps) {
  const { t, i18n } = useTranslation();
  const currentGame = useGameStore(state => state.currentGame);
  const timer = useGameStore(state => state.timer);
  const language = useSettingsStore(state => state.language);
  const setLanguage = useSettingsStore(state => state.setLanguage);
  const [showLangMenu, setShowLangMenu] = useState(false);

  const handleLanguageChange = async (lang: 'ru' | 'en') => {
    setLanguage(lang);
    await i18n.changeLanguage(lang);
    setShowLangMenu(false);
  };

  const difficultyLabels: Record<Difficulty, string> = {
    very_easy: t('game.difficulty.very_easy'),
    easy: t('game.difficulty.easy'),
    medium: t('game.difficulty.medium'),
    hard: t('game.difficulty.hard'),
    expert: t('game.difficulty.expert'),
  };

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-card border-b border-border">
      {/* Left: Difficulty Dropdown */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{t('game.difficulty.title')}:</span>
        <select
          value={currentGame?.difficulty || 'medium'}
          onChange={(e) => onDifficultyChange(e.target.value as Difficulty)}
          className={cn(
            "px-3 py-1 text-sm font-medium bg-secondary border border-border rounded-lg",
            "focus:outline-none focus:ring-2 focus:ring-primary",
            "cursor-pointer"
          )}
        >
          {difficulties.map((diff) => (
            <option key={diff} value={diff}>
              {difficultyLabels[diff]}
            </option>
          ))}
        </select>
      </div>

      {/* Right: Language + Timer */}
      <div className="flex items-center gap-3">
        {/* Language selector */}
        <div className="relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="p-1 hover:bg-accent rounded transition-colors"
          >
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          
          {showLangMenu && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowLangMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                <button
                  onClick={() => handleLanguageChange('ru')}
                  className={cn(
                    "w-full px-4 py-2 text-sm text-left hover:bg-accent transition-colors",
                    language === 'ru' && 'bg-primary/10 text-primary font-medium'
                  )}
                >
                  Русский
                </button>
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={cn(
                    "w-full px-4 py-2 text-sm text-left hover:bg-accent transition-colors",
                    language === 'en' && 'bg-primary/10 text-primary font-medium'
                  )}
                >
                  English
                </button>
              </div>
            </>
          )}
        </div>

        {/* Timer */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-mono">{formatTime(timer)}</span>
        </div>
      </div>
    </div>
  );
}

